import { useState, type ReactNode } from "react";
import { flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, type ColumnDef, type SortingState } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { cn } from "../lib/cn";
import { microcopy } from "../lib/microcopy";
import { Button } from "./button";
import { Input } from "./form";
import { Skeleton } from "./overlays";

/** A column may carry `meta: { className }`, applied to its header and cells (widths, or hiding a column on narrow screens). */
type ColumnMeta = { className?: string } | undefined;
const metaClass = (meta: unknown) => (meta as ColumnMeta)?.className;

export interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  loading?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  onRowClick?: (row: T) => void;
  emptyState?: ReactNode;
  pageSize?: number;
  className?: string;
  toolbar?: ReactNode;
  rowClassName?: (row: T) => string | undefined;
  dense?: boolean;
}

export function DataTable<T>({ columns, data, loading, searchable = false, searchPlaceholder = "Search…", onRowClick, emptyState, pageSize = 10, className, toolbar, rowClassName, dense }: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
  });

  const rows = table.getRowModel().rows;

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {searchable || toolbar ? (
        <div className="flex flex-wrap items-center gap-2">
          {searchable ? (
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-sand-500" />
              <Input value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder={searchPlaceholder} className="ps-9" />
            </div>
          ) : null}
          {toolbar ? <div className="ms-auto flex items-center gap-2">{toolbar}</div> : null}
        </div>
      ) : null}
      <div className="surface overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin" tabIndex={0}>
          <table className="w-full text-sm">
            <thead className="bg-sand-50/80 text-xs text-sand-700">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => {
                    const canSort = h.column.getCanSort();
                    const sorted = h.column.getIsSorted();
                    return (
                      <th key={h.id} className={cn("border-b border-sand-200 px-4 py-2.5 text-start font-medium", canSort && "cursor-pointer select-none hover:text-aeblack-950", metaClass(h.column.columnDef.meta))} onClick={canSort ? h.column.getToggleSortingHandler() : undefined}>
                        <span className="inline-flex items-center gap-1">
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {canSort ? sorted === "asc" ? <ArrowUp className="size-3" /> : sorted === "desc" ? <ArrowDown className="size-3" /> : <ArrowUpDown className="size-3 opacity-40" /> : null}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-sand-100">
                      {columns.map((_, j) => (
                        <td key={j} className="px-4 py-3"><Skeleton className="h-4" /></td>
                      ))}
                    </tr>
                  ))
                : rows.length === 0
                  ? (
                    <tr>
                      <td colSpan={columns.length} className="px-4 py-10 text-center text-sm text-muted-foreground">{emptyState ?? microcopy("noResults")}</td>
                    </tr>
                  )
                  : rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                      className={cn("border-b border-sand-100 transition-colors last:border-0", onRowClick && "cursor-pointer hover:bg-aegold-50/40", rowClassName?.(row.original))}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className={cn("px-4 align-middle", dense ? "py-2" : "py-3", metaClass(cell.column.columnDef.meta))}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                      ))}
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
        {table.getPageCount() > 1 ? (
          <div className="flex items-center justify-between border-t border-sand-200 px-4 py-2 text-xs text-muted-foreground">
            <span>
              {table.getState().pagination.pageIndex * pageSize + 1}–{Math.min((table.getState().pagination.pageIndex + 1) * pageSize, table.getFilteredRowModel().rows.length)} / {table.getFilteredRowModel().rows.length}
            </span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon-sm" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} aria-label={microcopy("previousPage")}><ChevronLeft className="rtl:rotate-180" /></Button>
              <span className="tabular">{table.getState().pagination.pageIndex + 1} / {table.getPageCount()}</span>
              <Button variant="ghost" size="icon-sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} aria-label={microcopy("nextPage")}><ChevronRight className="rtl:rotate-180" /></Button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
